// JSP content per task, ported as-is from the live app (esp-redirect js/node-jsp.js @ 9f451f0):
// TIER_CONFIG task tables, the added Tier 3 profiles and the PERMIT isolation table.
// One change: the Troubleshooting Need Power? line is the approved PLAN_WORDS.md wording (decision 49).
// Eight Tier 3 profiles are marked DRAFT in the live app. They stay marked here until their content is authored.
// Keyed by task name, the same key the live app and the task copy use. Generated: do not hand-edit lightly.

export const LIVE_TASK_CONTENT: Record<string, Record<string, unknown>> = {
 "Circuit Breaker Reset": {
  "tier": 1,
  "sop": {
   "num": "SOP-28.12a",
   "version": "2.0"
  },
  "brief": "Describe the observation — what are you looking for?",
  "controls": [
   "Know the trip type — overload, or short / ground fault.",
   "Handle only — panel stays closed.",
   "Stand to the side of the panel, not in front, on the handle.",
   "Off fully, then on."
  ],
  "door": "If a door must open to reach the handle → stop, full arc-rated PPE, escalate to Tier 2.",
  "prompts": [
   {
    "k": "causeKnown",
    "q": "Do you know what caused the trip — overload, or a short / ground fault?",
    "bad": "no",
    "why": "Reclosing into a short is how arc flash happens. NFPA 70E won’t let you re-energize blind.",
    "hazard": "arc",
    "elevates": true
   },
   {
    "k": "doorRequired",
    "q": "Do you need to open the panel to reach the handle?",
    "bad": "yes",
    "why": "Exposes energized bus. Shock boundaries active — this becomes Tier 2.",
    "escal": true
   }
  ],
  "doorBranch": null
 },
 "Overload Reset": {
  "tier": 1,
  "sop": {
   "num": "SOP-28.13",
   "version": "2.0"
  },
  "brief": "Describe the observation — what are you looking for?",
  "controls": [
   "Know why it overloaded — bind, bearing, phase loss, or load.",
   "Reset button only — enclosure stays closed.",
   "Let it cool before you reset.",
   "Trips again → stop. Repeated resets burn the motor."
  ],
  "door": "If the enclosure must open to reach the reset → this becomes Tier 2: de-energize (open the disconnect), verify dead, then reset with insulating gloves and an insulated tool.",
  "prompts": [
   {
    "k": "causeKnown",
    "q": "Do you know why it overloaded — bind, bearing, jam, phase loss, or load?",
    "bad": "no",
    "why": "An overload is the motor telling you it’s straining. Reset without the cause and it trips again — or you burn the motor.",
    "elevates": false
   },
   {
    "k": "repeatedTrips",
    "q": "Has this overloaded repeatedly without the cause being fixed?",
    "bad": "yes",
    "why": "Repeated resets cook the windings. Find the cause first.",
    "elevates": false
   },
   {
    "k": "cooldown",
    "q": "Has the overload had time to cool?",
    "bad": "no",
    "why": "A thermal overload won’t reset hot — forcing it damages the relay or motor.",
    "info": true,
    "elevates": false
   },
   {
    "k": "doorRequired",
    "q": "Do you need to open the enclosure to reach the reset?",
    "bad": "yes",
    "why": "The only safe way is de-energized: open the disconnect, verify dead, reset with gloves + an insulated tool. This becomes Tier 2.",
    "escal": true
   }
  ],
  "doorBranch": null
 },
 "Visual Inspection": {
  "tier": 1,
  "sop": {
   "num": "SOP-28.22",
   "version": "2.0"
  },
  "brief": "Describe the observation — what are you looking for?",
  "controls": [
   "Stay at normal working distance. Don’t touch or lean on the gear.",
   "Don’t open anything.",
   "Log every finding — even “no findings” is recorded."
  ],
  "door": "A finding that needs a look inside → stop, escalate to Tier 2.",
  "prompts": [],
  "doorBranch": null,
  "encl": "Enclosure closed and intact — external inspection only, no contact"
 },
 "VFD Parameters": {
  "tier": 1,
  "sop": {
   "num": "SOP-28.18",
   "version": "2.0"
  },
  "brief": "Describe the observation — what are you looking for?",
  "controls": [
   "Keypad / HMI only. No tools, no contact.",
   "Enclosure stays closed."
  ],
  "door": "If the door must open to reach the keypad → establish the approach boundary for others (below).",
  "prompts": [],
  "doorBranch": {
   "q": "Do you need to open the enclosure to reach the drive keypad?",
   "reasons": [
    {
     "v": "design",
     "label": "Drive keypad is inside — that’s how it’s built"
    },
    {
     "v": "broken",
     "label": "External interface damaged or inoperable"
    }
   ],
   "affirm": "Keypad only — stay outside the restricted boundary, no contact with exposed parts."
  },
  "encl": "Enclosure closed and intact — keypad access only, no exposure"
 },
 "PLC Interface Monitoring": {
  "tier": 1,
  "sop": {
   "num": "SOP-28.19",
   "version": "2.0"
  },
  "brief": "Describe the observation — what are you looking for?",
  "controls": [
   "Comm port from outside only — approved cable, no force.",
   "Don’t disturb other terminals or wiring."
  ],
  "door": "If the door must open to reach the port → establish the approach boundary for others (below).",
  "prompts": [],
  "doorBranch": {
   "q": "Do you need to open the enclosure to reach the comm port?",
   "reasons": [
    {
     "v": "design",
     "label": "Comm port is inside — that’s how it’s built"
    },
    {
     "v": "broken",
     "label": "External port damaged or inoperable"
    }
   ],
   "affirm": "Port and cable only — stay outside the restricted boundary, no contact with exposed parts."
  },
  "encl": "Enclosure closed and intact — external port access only, no exposure"
 },
 "Troubleshooting": {
  "tier": 2,
  "sop": {
   "num": "SOP-28.6",
   "version": "2.0"
  },
  "brief": "e.g. MCC-3 starter won’t pull in — checking for control voltage at the coil.",
  "justify": {
   "mode": "eswc",
   "line": "Most troubleshooting can be done de-energized. Think it through before you open it."
  },
  "cond": "Gear in normal operating condition — no abnormal sounds, smells, or heat marks before you approach.",
  "hazards": [
   "shock",
   "arc"
  ],
  "ppeMode": "full",
  "ppe": [],
  "verdictNote": "Live probing inside the boundary — arc-rated PPE and insulating gloves are the protection. Nothing here engineers the hazard out.",
  "boundary": "Mark and control the LAB and AF boundary. Unqualified persons excluded from the limited approach boundary.",
  "standby": "Second QEW staged outside the AF boundary — voice contact and line of sight.",
  "peak": "Highest arc-flash risk is at first probe contact and at door opening. Position and PPE matter more than technique.",
  "meterVerify": true,
  "doVerb": "Test",
  "technique": [
   "CAT III/IV meter for the voltage — finger-guarded probes, fuses in spec.",
   "Continuity and resistance: de-energized only.",
   "Minimum necessary — confirm, don’t linger. No energized repair; corrections go to ESWC."
  ],
  "prompts": []
 },
 "Voltage Testing": {
  "tier": 2,
  "sop": {
   "num": "SOP-28.6",
   "version": "2.0"
  },
  "brief": "e.g. Confirming 480V present at the DS-4 line side.",
  "justify": {
   "mode": "eswc",
   "line": "De-energized (ESWC per SOP-28.2) is the default. Energized voltage testing only when the check can’t be done dead — document why."
  },
  "cond": "Gear in normal operating condition — no abnormal sounds, smells, or heat marks before you approach.",
  "hazards": [
   "shock",
   "arc"
  ],
  "ppeMode": "full",
  "ppe": [],
  "verdictNote": "Testing live points inside the boundary — arc-rated PPE and gloves are your only barrier at first contact.",
  "boundary": "Mark and control the LAB and AF boundary. Unqualified persons excluded from the limited approach boundary.",
  "standby": "Second QEW staged outside the AF boundary — voice contact and line of sight.",
  "peak": "Highest arc-flash risk is at first probe contact and at door opening.",
  "meterVerify": true,
  "doVerb": "Test",
  "technique": [
   "CAT III/IV meter for the voltage — finger-guarded probes.",
   "Deliberate probe placement, steady hands, stable stance.",
   "Measuring live voltage — not confirming dead. Verify-dead is a separate ESWC/VAV path."
  ],
  "prompts": [
   {
    "k": "live",
    "q": "Measuring a live circuit — not using this test to confirm the circuit is de-energized?",
    "bad": "no",
    "why": "Confirming absence of voltage is verify-dead (LDL) under ESWC/VAV, not energized troubleshooting. Re-plan on that path.",
    "info": true
   }
  ]
 },
 "Current Measurements": {
  "tier": 2,
  "sop": {
   "num": "SOP-28.47",
   "version": "1.0"
  },
  "brief": "e.g. Reading running amps on the Pump 2 motor leads, L1–L3.",
  "justify": {
   "mode": "eswc",
   "line": "De-energized (ESWC) is the default. Energized clamp measurement only when the diagnostic can’t be done dead — document why."
  },
  "cond": "Gear in normal operating condition. Running-equipment measurement only — don’t measure gear de-energized mid-task without re-establishing ESWC.",
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "ppeMode": "full",
  "ppe": [],
  "verdictNote": "Clamp on live conductors inside the boundary — PPE and gloves carry the protection; jaw movement on tight bus is the risk.",
  "boundary": "Mark and control the LAB and AF boundary. Unqualified persons excluded.",
  "standby": "Second QEW staged outside the AF boundary — voice contact and line of sight.",
  "peak": "Highest arc-flash risk is at jaw placement and removal on tight bus-work. Slow, deliberate jaw movement.",
  "meterVerify": true,
  "doVerb": "Measure",
  "technique": [
   "True RMS clamp, CAT III min — non-True-RMS on VFD/soft-starter output is meaningless.",
   "SINGLE conductor per jaw — two or more cancel to a false near-zero.",
   "Jaw fully closed, perpendicular, centered. Compare all three phases — >5% imbalance = investigate."
  ],
  "prompts": [
   {
    "k": "single",
    "q": "Can the jaw close around a single conductor without touching adjacent phases?",
    "bad": "no",
    "why": "Spacing too tight for safe clamp placement — stop and request de-energization.",
    "escal": true
   },
   {
    "k": "rms",
    "q": "True RMS meter confirmed for this circuit?",
    "bad": "no",
    "why": "Non-True-RMS on VFD/soft-starter output produces meaningless readings.",
    "info": true
   }
  ]
 },
 "IR Thermography Survey": {
  "tier": 2,
  "sop": {
   "num": "SOP-28.20",
   "version": "2.0"
  },
  "brief": "e.g. Scanning the MCC-3 bus stabs and breaker lugs for hot spots.",
  "justify": {
   "mode": "inherent",
   "line": "This diagnostic is inherently energized and under load — dead gear shows no thermal signature. Confirm gear is at ≥40% of rated load, or the scan isn’t valid."
  },
  "cond": "Confirm normal operating condition before you touch a cover — missing hardware, damaged latches, evidence of impending failure = do not proceed.",
  "hazards": [
   "shock",
   "arc"
  ],
  "ppeMode": "ir",
  "ppe": [],
  "verdictNote": "Stand ≥3′06″ back with no covers removed → no arc-flash/shock PPE. Remove a bolted panel with a tool → arc-rated PPE and shock protection required.",
  "boundary": "Position yourself and the standby OUTSIDE the AF boundary. Don’t cross the restricted approach boundary with hands or body — zoom, don’t step in.",
  "standby": "Second qualified individual observing outside the AF boundary. (Contractor thermographer exception per SOP §2.)",
  "peak": "Highest risk is at the moment a cover comes off. Open only to the angle you need, stand to the hinge side, don’t lean in.",
  "meterVerify": false,
  "doVerb": "Scan",
  "technique": [
   "Camera on extension — zoom, don’t step in.",
   "Shoot lugs, bus stabs, breakers under load. Record load %, ambient, ΔT per finding.",
   "Under 30 seconds per position. Close and restore every latch and seal."
  ],
  "prompts": [
   {
    "k": "load",
    "q": "Is the gear at ≥40% of rated load?",
    "bad": "no",
    "why": "Under 40% the scan isn’t valid — low-grade resistive faults won’t show. Confirm and record load %.",
    "escal": true
   }
  ]
 },
 "Energized Inspection — Doors Open": {
  "tier": 2,
  "sop": {
   "num": "SOP-28.14",
   "version": "2.0"
  },
  "brief": "e.g. Inspecting inside Panel B — external scan flagged discoloration at the main lugs.",
  "justify": {
   "mode": "eswc-strong",
   "line": "Highest-risk Tier 2 task. Why is de-energized inspection insufficient? An external finding (SOP-28.22) must justify going in energized — do not perform casually."
  },
  "cond": "Gear in normal operating condition on the exterior walkaround before you open. Any abnormality — do not open.",
  "hazards": [
   "shock",
   "arc"
  ],
  "ppeMode": "full",
  "ppe": [],
  "verdictNote": "Doors-open observation on energized gear — arc-rated PPE is the barrier at the moment of opening, the highest-risk instant of the task.",
  "boundary": "Mark and control the LAB and AF boundary. Unqualified persons excluded from the limited approach boundary.",
  "standby": "Second QEW staged outside the AF boundary — voice contact, line of sight, ready to react.",
  "peak": "Arc-flash risk is HIGHEST at the moment of door opening — the act of opening can trigger it. Stand to the side, firm deliberate motion, do not partially open.",
  "meterVerify": false,
  "doVerb": "Observe",
  "technique": [
   "Observation only — no touch, no probe, no adjust.",
   "Look for heat marks, discoloration, loose terminations, contamination, damaged insulation.",
   "Any finding that needs work → close up and use ESWC. Never energized."
  ],
  "prompts": [
   {
    "k": "justified",
    "q": "Is there a documented external finding requiring an internal look?",
    "bad": "no",
    "why": "This task isn’t performed casually. Without a justification, close up and inspect de-energized.",
    "escal": true
   }
  ]
 },
 "Disconnect Operation": {
  "tier": 2,
  "sop": {
   "num": "SOP-28.25",
   "version": "2.0"
  },
  "brief": "e.g. Re-energizing DS-7 after replacing the Pump 2 starter.",
  "justify": {
   "mode": "switching",
   "line": "This task centers on re-energizing after a component was replaced or major work was done — the first throw after LOTO. NFPA 70E calls this out as the higher-risk case. Load interrupted via normal controls before any throw (120.5(2)) — disconnects aren’t load-break devices."
  },
  "cond": "Walk the outside — damage, discoloration, overheating marks, burning smell, unusual sounds. Latches, fasteners, tamper seal intact. Any abnormality — do not operate.",
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "ppeMode": "full",
  "ppe": [
   "Operating hand carries the primary exposure — gloves on both hands."
  ],
  "verdictNote": "Operating the handle on energized gear — arc-rated PPE protects against an arc venting at the throw; the enclosure is your only other barrier.",
  "boundary": "Approach from the SIDE of the enclosure, never directly in front. Body outside the line of fire from door hardware and vent openings.",
  "standby": "Second QEW staged outside the AF boundary — voice contact.",
  "peak": "The moment of the throw is the highest-risk moment. Worn contacts or a downstream fault can arc and vent through door seams. Face and head turned away at the throw.",
  "meterVerify": false,
  "doVerb": "Operate",
  "technique": [
   "One firm, full stroke — don’t hesitate, don’t partially operate.",
   "Resistance beyond normal → don’t force. A stuck disconnect is a maintenance ticket.",
   "Disconnects aren’t fault-clearing — repeated operation to clear a fault is prohibited."
  ],
  "prompts": []
 },
 "Fuse Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.7",
   "version": "2.0"
  },
  "brief": "e.g. Replacing the blown L2 fuse in the DS-4 fusible disconnect — cause confirmed as a failed downstream contactor.",
  "precondition": "Cause of the fuse blowing identified and understood FIRST. Unknown cause → troubleshoot (SOP-28.6) before replacing — a fresh fuse into a live fault arcs on close-in.",
  "sources": [
   {
    "id": "line",
    "label": "Line-side feed to the fuse holder"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "Fuses on capacitor banks, VFD DC bus, or control power sit downstream of stored energy — verify discharge to ZERO before handling. Don’t rely on time.",
  "verdictNote": "Fuse contact only after ESWC and LDL at the fuse terminals. The line side stays live until upstream isolation is proven — isolation, not PPE, is the control.",
  "ldlPoints": "At the fuse terminals — line and load clips, phase-to-phase and phase-to-ground.",
  "doVerb": "Replace",
  "doSteps": [
   "Exact-match replacement only — type, class, voltage, ampacity, interrupting rating. Never upsize to stop nuisance operation.",
   "Insulated fuse pullers rated for the size and voltage — stand to the side, never pull a fuse by hand.",
   "Inspect the holder and clips for damage, overheating, or discoloration before installing."
  ],
  "restoreNote": "Confirm the cause is corrected before the return-to-service throw — a fresh fuse into a live fault arcs on close-in.",
  "prompts": [
   {
    "k": "cause",
    "q": "Has the cause of the fuse blowing been identified and understood?",
    "bad": "no",
    "why": "Replacing without knowing why it blew can re-energize into a fault — troubleshoot first (SOP-28.6).",
    "escal": true
   }
  ],
  "permit": {
   "sop": "SOP-28.7",
   "ldlConfig": 1,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "One upstream source — the feed to the fuse holder. The holder’s LINE side stays live until the UPSTREAM disconnect is opened. The fuse is not its own isolation device.",
   "load": "Interrupt load and open the upstream disconnect. No motor load on this circuit.",
   "stored": "If this fuse protects a capacitor bank, VFD DC bus, or control power, that stored energy sits DOWNSTREAM — verify discharge to ZERO. Don’t rely on time.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "Exact-match fuse only — type, class, voltage, ampacity, interrupting rating.",
    "Insulated pullers, stand to the side, never pull by hand.",
    "Inspect holder and clips before installing."
   ],
   "doPrompt": {
    "q": "Has the cause of the fuse blowing been identified and understood?",
    "bad": "no",
    "why": "A fresh fuse into an uncorrected fault arcs on close-in — troubleshoot first (SOP-28.6)."
   },
   "restore": "Confirm the cause is corrected before the return-to-service throw."
  }
 },
 "Starter Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.8",
   "version": "2.0"
  },
  "brief": "e.g. Replacing the failed MCC-3 bucket starter feeding the #2 hammermill motor.",
  "precondition": "Line voltage AND control voltage are independent sources — control power is often fed separately. Both isolated, or the starter isn’t dead.",
  "sources": [
   {
    "id": "line",
    "label": "Line feed (480V)"
   },
   {
    "id": "control",
    "label": "Control power (separate source)"
   }
  ],
  "nonElecDefault": [
   "inertia"
  ],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "Control-transformer secondaries hold voltage briefly. Power-factor caps on the motor retain lethal DC. Verify ZERO on every line, load, and control terminal.",
  "verdictNote": "Line, load, and control are three separate energy sources. All isolated and proven dead at the starter terminals before you touch a conductor.",
  "ldlPoints": "Every line, load, and control terminal at the starter — phase-to-phase and phase-to-ground.",
  "doVerb": "Replace",
  "doSteps": [
   "Label every conductor — line, load, control — before disconnecting. Photograph the terminations first.",
   "Ground bond off LAST, on FIRST. Terminal torque to the manufacturer spec.",
   "Overloads set to the motor nameplate FLA after install — never left at factory default."
  ],
  "restoreNote": "Verify overload settings against nameplate FLA before return to service. Monitor the first start for correct rotation and normal current.",
  "prompts": [
   {
    "k": "control",
    "q": "Is control power confirmed isolated and locked as its own source?",
    "bad": "no",
    "why": "Control power is frequently a separate feed — isolating line alone leaves control conductors live.",
    "escal": true
   }
  ],
  "permit": {
   "sop": "SOP-28.8",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [
    "Mechanical",
    "Stored Energy"
   ],
   "sources": "TWO independent electrical sources — the line feed AND control power. Control power is frequently a SEPARATE feed; isolate each at its own source, or the starter isn’t dead.",
   "load": "Downstream motor — confirm it is stopped and cannot be back-driven by its load. A coasting or load-driven motor backfeeds as a generator.",
   "stored": "Control-transformer secondary holds voltage briefly. Power-factor caps on the motor retain lethal DC. Verify ZERO on every line, load, and control terminal.",
   "backfeed": "Motor-as-generator: the driven load can spin the motor and backfeed the starter after isolation.",
   "doVerb": "Replace",
   "doSteps": [
    "Label every conductor — line, load, control — before disconnecting. Photo first.",
    "Ground bond off LAST, on FIRST. Torque to spec.",
    "Overloads set to nameplate FLA after install — not factory default."
   ],
   "doPrompt": {
    "q": "Control power confirmed isolated and locked as its own source?",
    "bad": "no",
    "why": "Isolating line alone leaves control conductors live — control is a separate feed."
   },
   "restore": "Verify overloads against nameplate FLA. Monitor first start for rotation and normal current."
  }
 },
 "Transformer Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.43",
   "version": "1.0"
  },
  "brief": "e.g. Replacing the failed 45kVA dry-type transformer feeding Panel B — primary + three secondary circuits.",
  "precondition": "Two independent isolations: the PRIMARY feed AND every SECONDARY circuit. Opening the primary does NOT kill the secondary — downstream backfeed can re-energize it.",
  "sources": [
   {
    "id": "primary",
    "label": "Primary feed"
   },
   {
    "id": "sec",
    "label": "All secondary circuits (backfeed risk)"
   }
  ],
  "nonElecDefault": [],
  "ground": true,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "Dry core holds magnetic energy briefly. More critically — downstream drives, caps, and UPS on the secondary retain lethal DC. Verify DC absent at downstream terminals; don’t trust time.",
  "verdictNote": "Primary and secondary are independent sources. Both proven dead by LDL, independently, at both sets of terminals — backfeed is the killer here.",
  "ldlPoints": "Both primary terminals AND every secondary terminal — independently, phase-to-phase and phase-to-ground.",
  "doVerb": "Replace",
  "doSteps": [
   "Positively identify and label ALL secondary circuits before isolation — an unidentified secondary is an uncontrolled source.",
   "Label primary polarity and secondary phase arrangement to match the original.",
   "Rigging plan confirmed for transformer weight before the unit moves."
  ],
  "restoreNote": "Re-verify primary and secondary phasing before energizing. Remove temporary grounds only after the unit is fully reconnected.",
  "prompts": [
   {
    "k": "sec",
    "q": "Have ALL secondary circuits been positively identified and isolated?",
    "bad": "no",
    "why": "A secondary circuit not identified is a secondary not controlled — backfeed re-energizes the winding you’re holding.",
    "escal": true
   }
  ],
  "permit": {
   "sop": "SOP-28.43",
   "ldlConfig": 3,
   "grounds": true,
   "nonElecDefault": [],
   "sources": "TWO independent isolations — the PRIMARY feed AND every SECONDARY circuit. Opening the primary does NOT kill the secondary; the transformer cannot isolate its own secondary side.",
   "load": "Confirm every secondary circuit de-energized INDEPENDENTLY — downstream drives, UPS, or on-site generation can backfeed the secondary winding.",
   "stored": "Dry core holds magnetic energy briefly. Downstream drives, caps, and UPS on the secondary retain lethal DC — verify absent at downstream terminals; don’t trust time.",
   "backfeed": "Secondary backfeed is the killer here — a secondary you didn’t identify is a source you didn’t control.",
   "doVerb": "Replace",
   "doSteps": [
    "Positively identify and label ALL secondary circuits before isolation.",
    "Label primary polarity and secondary phasing to match the original.",
    "Rigging plan confirmed for transformer weight before the unit moves."
   ],
   "doPrompt": {
    "q": "Have ALL secondary circuits been positively identified and isolated?",
    "bad": "no",
    "why": "An unidentified secondary backfeeds the winding you’re holding."
   },
   "restore": "Re-verify primary and secondary phasing before energizing. Remove temporary grounds only after full reconnection."
  }
 },
 "Component Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.15",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Includes control components and electrical assemblies",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.15.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Exposed energized conductors supplied by panelboard or MCC.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.15",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Includes control components and electrical assemblies",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 },
 "Motor Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.9",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Exposed energized conductors at motor terminal box or MCC",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.9.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Exposed energized conductors supplied by panelboard or MCC.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.9",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Exposed energized conductors at motor terminal box or MCC",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 },
 "MCC Bucket Removal": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.23",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Insertion or removal of individual starter buckets from MCC",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.23.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Insertion or removal of individual starter buckets from MCC.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.23",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Insertion or removal of individual starter buckets from MCC",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 },
 "MCC Bucket Installation": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.24",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Insertion or removal of individual starter buckets from MCC",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.24.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Insertion or removal of individual starter buckets from MCC.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.24",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Insertion or removal of individual starter buckets from MCC",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 },
 "Control Wiring Modification": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.16",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Control circuits with exposed energized conductors greater than 120V",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.16.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Control circuits with exposed energized conductors >120V.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.16",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Control circuits with exposed energized conductors greater than 120V",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 },
 "Panel Breaker Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.44",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Panelboard and load centers, 480V and below. Bus de-energization required — adjacent breakers remain live.",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.44.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Removal or installation of CBs or switches.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.44",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Panelboard and load centers, 480V and below. Bus de-energization required — adjacent breakers remain live.",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 },
 "Feed Breaker Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.45",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Main or sub-feed breaker supplying panel or MCC. Upstream isolation required — planned outage, advance coordination needed.",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.45.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Removal or installation of CBs or switches.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.45",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Main or sub-feed breaker supplying panel or MCC. Upstream isolation required — planned outage, advance coordination needed.",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 },
 "Disconnect Switch Replacement": {
  "tier": 3,
  "sop": {
   "num": "SOP-28.46",
   "version": "draft"
  },
  "brief": "⚠ DRAFT PROFILE — workflow structure only, content not authored. Registry note: Field-mounted disconnect switch or safety switch assembly. Upstream LOTO required — disconnect handle is NOT a LOTO point for its own replacement.",
  "precondition": "⚠ DRAFT — precondition not authored for this task.",
  "sources": [
   {
    "id": "src1",
    "label": "⚠ DRAFT — source(s) not authored"
   }
  ],
  "nonElecDefault": [],
  "ground": false,
  "hazards": [
   "shock",
   "arc",
   "stored"
  ],
  "storedNote": "⚠ DRAFT — stored-energy note not authored. Confirm from SOP-28.46.",
  "verdictNote": "⚠ DRAFT — verdict note not authored. Registry basis: Removal or installation of CBs or switches.",
  "ldlPoints": "⚠ DRAFT — LDL test points not authored.",
  "doVerb": "Replace",
  "doSteps": [
   "⚠ DRAFT — technique not authored."
  ],
  "restoreNote": "⚠ DRAFT — restore note not authored.",
  "prompts": [],
  "permit": {
   "sop": "SOP-28.46",
   "ldlConfig": 3,
   "grounds": false,
   "nonElecDefault": [],
   "sources": "⚠ DRAFT — source description not authored. Registry note: Field-mounted disconnect switch or safety switch assembly. Upstream LOTO required — disconnect handle is NOT a LOTO point for its own replacement.",
   "load": "⚠ DRAFT — load-interruption note not authored.",
   "stored": "⚠ DRAFT — stored-energy note not authored.",
   "backfeed": null,
   "doVerb": "Replace",
   "doSteps": [
    "⚠ DRAFT — technique not authored."
   ],
   "doPrompt": {
    "q": "⚠ DRAFT",
    "bad": "no",
    "why": "⚠ DRAFT"
   },
   "restore": "⚠ DRAFT — restore note not authored."
  }
 }
};
